/**
 * 지출·경비 — [지출 현황] 통계 탭 (v2.53.0).
 *
 * 시야는 서버(GET /expense/stats)가 문서 열람 규칙과 동일하게 자른다:
 *   일반 직원 = 본인 지출만 / 부서장 = 자기 부서 / 경영지원·대표이사·admin = 전사.
 * 집계 기준 (2026-08-05 사용자 확정 · 문서종류는 2026-08-23 v2.84.0 개정):
 *   - ⭐문서종류 = **법인카드+개인경비만** — 구입지급결의서는 물품 대금이라 통계에서 제외
 *     (문서함 목록에는 그대로 보임. 옛 '구입·지급' 계정과목 축도 이로써 사라짐)
 *   - 기본 = 최종승인 문서만 ([결재 진행 중 포함] 스위치로 검토대기~경영지원승인 추가)
 *   - 날짜 = 행의 사용일 ("언제 쓴 돈인가")
 *   - 금액 = 부가세 포함가 · 카드 취소(음수) 반영
 * 차트는 매출 분석과 같은 자체 SVG (외부 라이브러리 없음).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as api from '../../lib/expense-api';
import { VehicleManageModal } from '../../components/VehicleManageModal';
import { ExpenseDrillModal, type DrillCrumb } from '../../components/ExpenseDrillModal';
import { LoadingLines } from '../../components/ui/Skeleton';

const fmtKrw = (n: number): string => Math.round(n).toLocaleString('ko-KR');

/** 축약 금액 — 1.2억 / 3,400만 / 9,900 */
function fmtShort(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 100_000_000) return `${sign}${(abs / 100_000_000).toFixed(1)}억`;
  if (abs >= 10_000) return `${sign}${Math.round(abs / 10_000).toLocaleString('ko-KR')}만`;
  return `${sign}${Math.round(abs).toLocaleString('ko-KR')}`;
}

const YEARS = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i);

/**
 * 통계 집계 대상 문서종류 (v2.84.0 · 2026-08-23 사용자 확정) — **법인카드+개인경비만.**
 * 구입지급결의서는 물품 대금이라 경비 통계에서 제외 (서버 /stats 도 같은 규칙 — 여기만
 * 바꾸면 안 된다). 문서함 목록에는 구입지급도 그대로 보인다.
 */
const STAT_DOC_TYPES = ['개인경비정산서', '법인카드사용내역서'] as const;
type StatDocType = (typeof STAT_DOC_TYPES)[number];
const TYPE_COLORS: Record<StatDocType, string> = {
  개인경비정산서: '#10b981',
  법인카드사용내역서: '#f59e0b',
};
const TYPE_SHORT: Record<StatDocType, string> = {
  개인경비정산서: '개인경비',
  법인카드사용내역서: '법인카드',
};

export function ExpenseStatsTab() {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState<number | ''>('');
  const [type, setType] = useState<StatDocType | ''>('');
  const [dept, setDept] = useState('');
  const [author, setAuthor] = useState('');
  const [account, setAccount] = useState('');
  const [includePending, setIncludePending] = useState(false);
  /**
   * 지출 상세 탐색 (v2.63.0) — 계정과목·사용자 줄 클릭 → 드릴다운 창.
   * 창을 여는 순간의 화면 필터를 바탕 조건으로 넘긴다 (창 안 경로와 별개).
   */
  const [drill, setDrill] = useState<DrillCrumb[] | null>(null);
  const drillBase: api.ExpenseDrillParams = {
    year,
    month: month === '' ? undefined : month,
    type: type || undefined,
    dept: dept || undefined,
    author: author || undefined,
    account: account || undefined,
    includePending: includePending || undefined,
  };

  const statsQuery = useQuery({
    queryKey: ['expense', 'stats', year, month, type, dept, author, account, includePending],
    queryFn: () => api.getExpenseStats({
      year,
      month: month === '' ? undefined : month,
      type: type || undefined,
      dept: dept || undefined,
      author: author || undefined,
      account: account || undefined,
      includePending,
    }),
    staleTime: 30_000,
  });
  const data = statsQuery.data ?? null;
  const scopeLevel = data?.scope.level ?? 'self';

  // 부서·사용자 필터 옵션 (전사·부서 시야일 때만 노출)
  const users = useQuery({
    queryKey: ['expense', 'users'],
    queryFn: api.listPortalUsers,
    staleTime: 300_000,
    enabled: scopeLevel !== 'self',
  });
  const deptOptions = useMemo(() => {
    const set = new Set<string>();
    for (const u of users.data ?? []) if (u.department) set.add(u.department);
    return [...set].sort();
  }, [users.data]);

  // 연 누계는 월 필터와 무관하게 12개월 합 (months 는 월 필터를 안 받는다) —
  // 월을 골라도 첫 카드가 둘째 카드와 같은 값이 되지 않도록 (2026-08-05 리뷰 반영)
  const yearSum = useMemo(
    () => (data?.months ?? []).reduce((s, m) => s + m.total, 0),
    [data],
  );
  const thisMonthAmount = useMemo(() => {
    if (!data) return 0;
    if (month !== '') return data.months[month - 1]?.total ?? 0;
    if (year !== currentYear) return 0;
    return data.months[currentMonth - 1]?.total ?? 0;
  }, [data, month, year, currentYear, currentMonth]);

  const maxAccount = Math.max(...(data?.byAccount ?? []).map((r) => Math.abs(r.amount)), 1);

  const selCls = 'ui-input';
  const isEmpty = !!data && data.summary.docCount === 0 && data.summary.pendingCount === 0;
  // 필터 때문에 0건인지, 정말 아무 문서도 없는지 구분해서 안내 (2026-08-05 리뷰 반영)
  const hasFilter = month !== '' || type !== '' || dept !== '' || author !== '' || account !== '';

  const scopeBadge = scopeLevel === 'all'
    ? { text: '전사 전체', cls: 'bg-blue-50 text-blue-700 border-blue-200' }
    : scopeLevel === 'dept'
      ? { text: `내 부서(${(data?.scope.depts ?? []).join('·')}) + 내 문서`, cls: 'bg-sky-50 text-sky-700 border-sky-200' }
      : { text: '내 지출만', cls: 'bg-slate-100 text-slate-600 border-slate-200' };

  return (
    <div className="space-y-4">
      {/* 필터 + 요약 */}
      <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="ui-section-title">지출 현황</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${scopeBadge.cls}`}>
              {scopeBadge.text}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select className={selCls} value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {YEARS.map((y) => <option key={y} value={y}>{y}년</option>)}
            </select>
            <select className={selCls} value={month}
              onChange={(e) => setMonth(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">전체 월</option>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>{m}월</option>
              ))}
            </select>
            <select className={selCls} value={type}
              onChange={(e) => setType(e.target.value as StatDocType | '')}>
              <option value="">전체 종류</option>
              {STAT_DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className={selCls} value={account} onChange={(e) => setAccount(e.target.value)}>
              <option value="">전체 계정과목</option>
              {api.EXPENSE_ACCOUNTS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            {scopeLevel !== 'self' && (
              <>
                <select className={selCls} value={dept} onChange={(e) => setDept(e.target.value)}>
                  <option value="">전체 부서</option>
                  {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                <select className={selCls} value={author} onChange={(e) => setAuthor(e.target.value)}>
                  <option value="">전체 사용자</option>
                  {(users.data ?? []).map((u) => (
                    <option key={u.userId} value={u.userId}>{u.name}{u.department ? ` (${u.department})` : ''}</option>
                  ))}
                </select>
              </>
            )}
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={includePending}
                onChange={(e) => setIncludePending(e.target.checked)} />
              결재 진행 중 포함
            </label>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded px-4 py-3">
            <div className="text-xs text-slate-500">{year}년 누계</div>
            <div className="text-lg font-bold text-slate-900 num">{fmtKrw(yearSum)}원</div>
          </div>
          <div className="bg-slate-50 rounded px-4 py-3">
            <div className="text-xs text-slate-500">
              {month !== '' ? `${month}월 지출` : year === currentYear ? `이번 달 (${currentMonth}월)` : '월 선택 없음'}
            </div>
            <div className="text-lg font-bold text-slate-900 num">{fmtKrw(thisMonthAmount)}원</div>
          </div>
          <div className="bg-slate-50 rounded px-4 py-3">
            <div className="text-xs text-slate-500">{includePending ? '집계 문서 (진행 중 포함)' : '완결(최종승인) 문서'}</div>
            <div className="text-lg font-bold text-slate-900 num">{data?.summary.docCount ?? 0}건</div>
          </div>
          <div className="bg-slate-50 rounded px-4 py-3">
            <div className="text-xs text-slate-500">결재 진행 중 (연간·전체 과목)</div>
            <div className="text-lg font-bold text-slate-900 num">
              {data?.summary.pendingCount ?? 0}건
              <span className="text-xs font-normal text-slate-500 ml-1">{fmtKrw(data?.summary.pendingAmount ?? 0)}원</span>
            </div>
          </div>
        </div>

        <div className="mt-2 text-xs text-slate-500">
          {/* v2.134.0 — 카드 문서 목록이 [지출결재 문서] 탭에서 빠졌다. 여기서 카드 금액을 보고
              문서를 찾으러 갔다가 못 찾는 일이 없도록 어느 탭에 있는지 함께 적는다. */}
          집계 기준: <b>법인카드 + 개인경비만</b> (구입지급결의서는 물품 대금이라 제외 —
          그 문서와 지출 분류별 금액은 [지출결재 문서] 탭에서 · 법인카드 <b>문서 목록</b>은 [법인카드 사용내역] 탭에서) ·
          최종승인 문서{includePending ? ' + 결재 진행 중' : ''} · 날짜=사용일 ·
          부가세 포함가 · 카드 취소(마이너스) 반영 ·
          [결재 진행 중] 카드는 월·계정과목 필터와 무관한 연간 기준
        </div>
      </div>

      {statsQuery.isLoading ? (
        <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center text-sm text-slate-400"><LoadingLines /></div>
      ) : statsQuery.isError ? (
        <div className="bg-white rounded shadow-sm border border-rose-200 p-6 text-sm text-rose-600">
          통계를 불러오지 못했습니다: {(statsQuery.error as Error).message}
        </div>
      ) : isEmpty ? (
        <div className="bg-white rounded shadow-sm border border-slate-200 p-10 text-center">
          <div className="text-3xl mb-2">📊</div>
          <div className="text-sm text-slate-500">
            {hasFilter ? (
              <>지금 고른 조건(월·종류·계정과목·부서·사용자)에 맞는 지출 문서가 없습니다.<br />
                필터를 넓혀 보세요.</>
            ) : (
              <>{year}년에 집계할 지출 문서가 아직 없습니다.<br />
                결재가 <b>최종승인</b>까지 끝나면 여기에 자동으로 집계됩니다.</>
            )}
          </div>
        </div>
      ) : data && (
        <>
          {/* 월별 추이 */}
          <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <h3 className="ui-subsection-title">월별 지출 추이 — {year}년</h3>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                {STAT_DOC_TYPES.map((t) => (
                  <span key={t} className="flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: TYPE_COLORS[t] }} />
                    {TYPE_SHORT[t]}
                  </span>
                ))}
              </div>
            </div>
            <MonthlyChart months={data.months} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 계정과목별 — 행 클릭 = 지출 상세 탐색 (v2.63.0) */}
            <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
              <h3 className="ui-subsection-title mb-1">계정과목별</h3>
              <p className="text-xs text-slate-500 mb-3">
                행을 클릭하면 상세 탐색이 열립니다 — 보조과목·사용자·월·사용처로 계속 파고들 수 있습니다
              </p>
              {data.byAccount.length === 0 ? (
                <div className="text-xs text-slate-500 py-4 text-center">데이터 없음</div>
              ) : (
                <div className="space-y-1.5">
                  {data.byAccount.map((r) => (
                    <button key={r.account} type="button"
                      onClick={() => setDrill([{ dim: 'account', value: r.account, label: r.account }])}
                      className="w-full grid grid-cols-[92px_minmax(0,1fr)_110px] items-center gap-2 text-left rounded px-1.5 py-1 hover:bg-blue-50"
                      title={`${r.account} — 클릭하면 상세 탐색이 열립니다`}>
                      <span className="text-xs text-slate-600 truncate">{r.account}</span>
                      <span className="block h-2.5 rounded-sm bg-slate-100 overflow-hidden">
                        <span className="block h-full rounded-sm"
                          style={{
                            width: `${Math.round((Math.max(r.amount, 0) / maxAccount) * 100)}%`,
                            background: '#10b981',
                          }} />
                      </span>
                      <span className="text-xs text-right num text-slate-700">
                        {fmtKrw(r.amount)}원 <span className="text-slate-400">({r.count})</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 사용자별 — 행 클릭 = 지출 상세 탐색 (v2.63.0) */}
            <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
              <h3 className="ui-subsection-title mb-1">사용자별</h3>
              <p className="text-xs text-slate-500 mb-3">
                {scopeLevel === 'self' ? '내 지출만 집계됩니다' : '내 시야 범위 안의 사용자만 표시됩니다'}
                {' '}— 행을 클릭하면 그 사람의 지출을 파고들 수 있습니다
              </p>
              {data.byUser.length === 0 ? (
                <div className="text-xs text-slate-500 py-4 text-center">데이터 없음</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-xs text-slate-500">
                        <th className="py-1.5 pr-2">이름</th>
                        <th className="py-1.5 pr-2">부서</th>
                        <th className="py-1.5 pr-2 text-right">문서</th>
                        <th className="py-1.5 text-right">금액</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byUser.map((r) => (
                        <tr key={r.userId}
                          onClick={() => setDrill([{ dim: 'author', value: r.userId, label: r.name }])}
                          title={`${r.name} — 클릭하면 상세 탐색이 열립니다`}
                          className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                          <td className="py-1.5 pr-2 text-slate-700 whitespace-nowrap">{r.name}</td>
                          <td className="py-1.5 pr-2 text-slate-500 whitespace-nowrap">{r.dept ?? '-'}</td>
                          <td className="py-1.5 pr-2 text-right num text-slate-500">{r.docCount}건</td>
                          <td className="py-1.5 text-right num font-medium text-slate-800 whitespace-nowrap">{fmtKrw(r.amount)}원</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* 사용대상별 상세 (v2.54.0) — 거래처별 접대비 · 차량별 유지비 · 직원별 복리후생비 · 목적지별 여비 */}
          <TargetSection byTarget={data.byTarget} byVehicle={data.byVehicle} onDrill={setDrill} />

          {/* 사용처 TOP */}
          <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
            <h3 className="ui-subsection-title mb-1">사용처 TOP {data.byVendor.length}</h3>
            <p className="text-xs text-slate-500 mb-3">카드 가맹점·경비 상호 — 금액 순</p>
            {data.byVendor.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">데이터 없음</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-xs text-slate-500">
                      <th className="py-1.5 pr-2 w-[36px]">순위</th>
                      <th className="py-1.5 pr-2">사용처</th>
                      <th className="py-1.5 pr-2 text-right">건수</th>
                      <th className="py-1.5 text-right">금액</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byVendor.map((r, i) => (
                      <tr key={r.vendor} className="border-t border-slate-100">
                        <td className="py-1.5 pr-2 text-slate-400">{i + 1}</td>
                        <td className="py-1.5 pr-2 text-slate-700">{r.vendor}</td>
                        <td className="py-1.5 pr-2 text-right num text-slate-500">{r.count}건</td>
                        <td className="py-1.5 text-right num font-medium text-slate-800 whitespace-nowrap">{fmtKrw(r.amount)}원</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* 지출 상세 탐색 (v2.63.0) — 계정과목·사용자 줄 클릭으로 열림 */}
      {drill && (
        <ExpenseDrillModal
          base={drillBase}
          baseAuthorName={(users.data ?? []).find((u) => u.userId === author)?.name}
          initialPath={drill}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

/** 사용대상 축의 이름 — 계정과목별로 뜻이 다르다 (법인카드 입력 규칙과 동일) */
const TARGET_NOUN: Record<string, string> = {
  접대비: '거래처',
  차량유지비: '차량',
  복리후생비: '직원',
  여비교통비: '목적지',
};

/**
 * 사용대상별 상세 (v2.54.0) — "거래처별 접대비", "차량별 유지비" 같은 세부 통계.
 * 원천 = 법인카드사용내역서의 [사용대상] 칸. 복리후생비는 함께 쓴 직원 수로 1/N 배분
 * (배분 합 = 원금액 — 서버 계산). 검색으로 특정 거래처·차량을 바로 찾는다.
 *
 * ⭐v2.61.0 — **차량유지비만 다르게 센다.** 사용대상이 자유 입력이라 같은 차가 여러 표기로
 *   갈라지므로(「6526」/「194호 6526」), 차량 기본정보로 묶은 서버 계산(byVehicle)을 쓴다.
 *   나머지 과목(거래처·직원·목적지)은 지금까지처럼 적힌 글자 그대로다.
 */
function TargetSection({
  byTarget, byVehicle, onDrill,
}: {
  byTarget: api.ExpenseStats['byTarget'];
  byVehicle: api.ExpenseStats['byVehicle'];
  /** 줄 클릭 → 상세 탐색 (v2.64.0). 계정과목 + 사용대상 두 단계를 한 번에 쌓는다 */
  onDrill: (path: DrillCrumb[]) => void;
}) {
  const accounts = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of byTarget) seen.set(r.account, (seen.get(r.account) ?? 0) + r.amount);
    return [...seen.entries()].sort((a, b) => b[1] - a[1]).map(([acc]) => acc);
  }, [byTarget]);
  const [acc, setAcc] = useState<string>('');
  const [search, setSearch] = useState('');
  const [showVehicles, setShowVehicles] = useState(false);
  // 데이터가 바뀌어 선택 과목이 사라지면 첫 과목으로
  const active = accounts.includes(acc) ? acc : (accounts[0] ?? '');
  const isCar = active === '차량유지비';

  const rows = useMemo(() => {
    const s = search.trim();
    if (isCar) {
      // 차량별 — 등록 차량으로 묶은 것. 안 붙은 표기는 아래 경고줄에서 따로 보여준다.
      // rawTargets = 이 차로 묶인 원본 표기들 — 탐색 창에 그대로 넘겨야 금액이 같아진다.
      return byVehicle.rows
        .map((v) => ({
          target: v.name, amount: v.amount, count: v.count,
          model: v.model, status: v.status, rawTargets: v.rawTargets,
        }))
        .filter((r) => !s || r.target.includes(s) || (r.model ?? '').includes(s));
    }
    return byTarget
      .filter((r) => r.account === active)
      .filter((r) => !s || r.target.includes(s))
      .map((r) => ({
        target: r.target, amount: r.amount, count: r.count,
        model: '', status: '', rawTargets: [r.target],
      }));
  }, [byTarget, byVehicle, active, isCar, search]);
  const maxAmt = Math.max(...rows.map((r) => Math.abs(r.amount)), 1);
  const noun = TARGET_NOUN[active] ?? '대상';

  return (
    <div className="bg-white rounded shadow-sm border border-slate-200 p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="ui-subsection-title">
          사용대상별 상세
          {active && <span className="font-normal text-slate-500 ml-2 text-xs">{active} → {noun}별</span>}
        </h3>
        <div className="flex items-center gap-2">
          {isCar && (
            <button type="button" onClick={() => setShowVehicles(true)}
              className="btn btn-secondary btn-sm">
              🚗 차량 관리
            </button>
          )}
          {accounts.length > 0 && (
            <input className="ui-input ui-input-sm w-44"
              placeholder={`${noun} 검색`}
              value={search} onChange={(e) => setSearch(e.target.value)} />
          )}
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        법인카드사용내역서의 [사용대상] 칸 기준 — 접대비=거래처 · 차량유지비=차량번호 · 복리후생비=직원 · 여비교통비=목적지.
        복리후생비는 함께 쓴 인원 수로 나눠 1인분씩 배분합니다.
        {isCar && ' 차량은 적힌 표기가 달라도 [차량 기본정보]로 묶어서 셉니다.'}
        {' '}<b className="text-slate-500">줄을 클릭하면 그 {noun}의 내역만 따로 파고들 수 있습니다.</b>
      </p>
      {isCar && byVehicle.unmatched.length > 0 && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3 text-amber-900">
          <b>어느 차에도 안 붙은 표기가 {byVehicle.unmatched.length}가지 있습니다</b> — 아래 합계에서 빠져 있습니다.
          <div className="mt-1 space-y-0.5">
            {byVehicle.unmatched.map((u) => (
              <button key={u.target} type="button"
                onClick={() => onDrill([
                  { dim: 'account', value: '차량유지비', label: '차량유지비' },
                  { dim: 'usageTarget', value: u.target, values: [u.target], label: u.target },
                ])}
                className="w-full flex justify-between gap-3 text-left rounded px-1 hover:bg-amber-100"
                title={`「${u.target}」 내역을 열어 봅니다`}>
                <span>「{u.target}」 <span className="text-amber-600">({u.reason})</span></span>
                <span className="num">{fmtKrw(u.amount)}원 ({u.count}건)</span>
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setShowVehicles(true)}
            className="mt-1 underline text-amber-800">[차량 관리]에서 별칭 추가하기</button>
        </div>
      )}
      {showVehicles && <VehicleManageModal onClose={() => setShowVehicles(false)} />}
      {accounts.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center">
          아직 사용대상이 입력된 내역이 없습니다 — 법인카드사용내역서에서 [사용대상]을 채우면 여기에 집계됩니다.
        </div>
      ) : (
        <>
          <div className="flex gap-1.5 flex-wrap mb-3">
            {accounts.map((a) => (
              <button key={a} type="button"
                onClick={() => { setAcc(a); setSearch(''); }}
                className={`btn btn-sm ${active === a ? 'btn-primary' : 'btn-secondary'}`}>
                {a} <span className={active === a ? 'text-blue-100' : 'text-slate-400'}>
                  ({TARGET_NOUN[a] ?? '대상'})
                </span>
              </button>
            ))}
          </div>
          {rows.length === 0 ? (
            <div className="text-xs text-slate-500 py-4 text-center">검색과 일치하는 {noun}이(가) 없습니다</div>
          ) : (
            <div className="space-y-1">
              {rows.map((r) => (
                <button key={r.target} type="button"
                  onClick={() => onDrill([
                    { dim: 'account', value: active, label: active },
                    { dim: 'usageTarget', value: r.rawTargets[0] ?? r.target, values: r.rawTargets, label: r.target },
                  ])}
                  className="w-full grid grid-cols-[minmax(90px,180px)_minmax(0,1fr)_120px] items-center gap-2 text-left rounded px-1.5 py-0.5 hover:bg-blue-50"
                  title={`${r.target}${r.model ? ` · ${r.model}` : ''}${r.status === '계약만료' ? ' · 계약만료' : ''} — 클릭하면 이 ${noun}의 ${active} 내역만 봅니다`}>
                  <span className="text-xs text-slate-700 truncate">
                    {r.target}
                    {r.model && <span className="text-slate-400 ml-1">{r.model}</span>}
                    {r.status === '계약만료' && <span className="text-slate-400 ml-1">(만료)</span>}
                  </span>
                  <span className="block h-2.5 rounded-sm bg-slate-100 overflow-hidden">
                    <span className="block h-full rounded-sm bg-violet-500"
                      style={{ width: `${Math.round((Math.max(r.amount, 0) / maxAmt) * 100)}%` }} />
                  </span>
                  <span className="text-xs text-right num text-slate-700">
                    {fmtKrw(r.amount)}원 <span className="text-slate-400">({r.count}건)</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * 월별 스택 막대 차트 — 매출 분석과 같은 자체 SVG (의존성 없음).
 * 문서종류 3색 스택. 음수(카드 취소)가 월 합계를 음수로 만들 수 있으나
 * 실사용에서 월 전체가 음수인 경우는 없다고 보고 0 으로 눌러 그린다(표시 값은 원본).
 */
function MonthlyChart({ months }: { months: api.ExpenseStats['months'] }) {
  const W = 960;
  const H = 230;
  const left = 56;
  const base = 196;
  const plotH = 168;
  const step = (W - left - 12) / 12;
  const barW = Math.min(46, step * 0.58);
  const max = Math.max(...months.map((m) => m.total), 1);
  const scale = (v: number) => (Math.max(v, 0) / max) * plotH;

  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[720px]" role="img"
        aria-label="월별 지출 추이 차트">
        {[0, 0.5, 1].map((f) => (
          <g key={f}>
            <line x1={left} x2={W - 8} y1={base - plotH * f} y2={base - plotH * f}
              stroke="#e2e8f0" strokeWidth="1" strokeDasharray={f === 0 ? '' : '3 3'} />
            <text x={left - 6} y={base - plotH * f + 4} textAnchor="end" fontSize="11" fill="#94a3b8">
              {fmtShort(max * f)}
            </text>
          </g>
        ))}
        {months.map((mo, i) => {
          const x = left + step * i + (step - barW) / 2;
          let cum = 0;
          return (
            <g key={mo.m}>
              <title>{`${mo.m}월 합계 ${fmtKrw(mo.total)}원`}</title>
              {(Object.keys(TYPE_COLORS) as StatDocType[]).map((t) => {
                const v = mo.byType[t] ?? 0;
                const h = scale(v);
                const y = base - cum - h;
                cum += h;
                if (h <= 0) return null;
                return <rect key={t} x={x} y={y} width={barW} height={h} fill={TYPE_COLORS[t]} rx="1.5" />;
              })}
              <text x={x + barW / 2} y={base + 16} textAnchor="middle" fontSize="11" fill="#64748b">
                {mo.m}월
              </text>
              {mo.total > 0 && (
                <text x={x + barW / 2} y={base - scale(mo.total) - 5} textAnchor="middle"
                  fontSize="10" fill="#64748b">
                  {fmtShort(mo.total)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
